import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../libs/shared/helpers/src/internationalization/react';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import headerImage from '../../assets/images/viewWithAssignee.svg';
import {useAppDispatch} from '../../helpers/reduxHelpers';
import {OnboardingTipTypesEnum} from '../../models/api/preferencesModel';
import {showCreateLensDialog} from '../../store/panels/inboxes/inboxesPanelActions';
import {InboxesPanelId} from '../../store/panels/panelStateBase';
import {Image} from '../common/image';
import {InfoBubble, InfoBubbleInstanceProps} from '../common/infoBubbles/infoBubble';
import {InfoBubbleContent} from '../common/infoBubbles/infoBubbleContent';
import {InfoBubbleDescription} from '../common/infoBubbles/infoBubbleContentDescription';
import {InfoBubbleTitle} from '../common/infoBubbles/infoBubbleContentTitle';
import {InfoBubbleFooterActions} from '../common/infoBubbles/infoBubbleFooterActions';
import {InfoBubblePill, InfoBubblePillVariantsEnum} from '../common/infoBubbles/infoBubblePill';
import {useInfoBubblesContext} from '../common/infoBubbles/infoBubblesContext';

/*
 * Props.
 */

interface LensesAssigneeSearchInfoBubbleProps extends InfoBubbleInstanceProps {
  panelId: InboxesPanelId;
}

/*
 * Styles.
 */

const StyledBubbleBodyDiv = styled.div`
  padding: 0px 28px 21px 28px;
`;

/*
 * Component.
 */

export const LensesAssigneeSearchInfoBubble: FC<LensesAssigneeSearchInfoBubbleProps> = ({
  targetRef,
  panelId,
}) => {
  const context = useInfoBubblesContext();
  const dispatch = useAppDispatch();
  const onClose = () => context.closeInfoBubbles(OnboardingTipTypesEnum.LENSES_ASSIGNEE_SEARCH);
  const onShowMe = () => {
    onClose();
    dispatch(showCreateLensDialog(panelId));
  };

  return (
    <InfoBubble
      {...context}
      position="right-end"
      type={OnboardingTipTypesEnum.LENSES_ASSIGNEE_SEARCH}
      targetRef={targetRef}
      isInteractive
      interactionId="lenses-assignee-search-bubble"
      noBubble
    >
      <InfoBubbleContent $removePadding>
        <Image src={headerImage} width={380} height={142} />
        <StyledBubbleBodyDiv>
          <InfoBubblePill type={InfoBubblePillVariantsEnum.EDUCATION} />
          <InfoBubbleTitle shouldWrapTitle>
            <Trans id="infobubble-lenses-assignee-search-title">
              Constantly searching with the same assignee?
            </Trans>
          </InfoBubbleTitle>
          <InfoBubbleDescription>
            <Trans id="infobubble-lenses-assignee-search-description">
              If you want to monitor conversations assigned to this teammate you can instead create a view
              with this assignee across all relevant inboxes.
            </Trans>
          </InfoBubbleDescription>
          <InfoBubbleFooterActions $justifyContentRight>
            <Button onClick={onClose} size={VisualSizesEnum.MEDIUM} variant={ButtonVariantsEnum.SECONDARY}>
              <Trans id="infobubble-lenses-assignee-search-not-now">Not now</Trans>
            </Button>
            <Button onClick={onShowMe} size={VisualSizesEnum.MEDIUM} variant={ButtonVariantsEnum.PRIMARY}>
              <Trans id="infobubble-lenses-assignee-search-show-me">Show me</Trans>
            </Button>
          </InfoBubbleFooterActions>
        </StyledBubbleBodyDiv>
      </InfoBubbleContent>
    </InfoBubble>
  );
};
